import React, { useEffect, useState } from 'react'
import {
  Container,
  Button,
  TextBody,
  TextTitle,
  TextCaption,
  Checkbox,
  FieldPassword,
  RadioGroup,
  Icon64,
  Image,
  Link,
  Icon
} from '@pmi/pmi-iqos-ds-react'
import {
  analytics,
  PaymentTypes,
  htmlTranslation,
  goTo,
  Routes,
  Fragments,
  Sites
} from '@pmi/pmi-iqos-shared-react'
import { CheckoutCreditCard } from '@zyn-common/components'

const CheckoutStep3 = ({
  allowStoringCard,
  translations,
  paymentMethods,
  selectedPaymentMethodIndex,
  handlePaymentMethodsChange,
  handleCheckboxChange,
  termsAndConds,
  phone,
  setMainStateProperty,
  tobaccoPassword,
  purchase,
  isPurchaseEnabled,
  hasTobacco,
  hasAdyenPaymentMethod,
  clearAdyenCheckoutForm,
  products,
  isOpen,
  paymentMethodsIcons,
  toggleStorePaymentMethod,
  handleDeleteCreditCard,
  handleEditCreditCard,
  isPaymentIdSet,
  isSagawa,
  line
}) => {
  const [analyticsFired, setAnalyticsFired] = useState(false)

  useEffect(() => {
    if (!analyticsFired && isOpen && isPaymentIdSet) {
      setAnalyticsFired(true)
      const type = paymentMethods[selectedPaymentMethodIndex].paymentType
      const hasStoredCards =
        type === PaymentTypes.CREDIT_CARD &&
        paymentMethods[selectedPaymentMethodIndex].cards &&
        paymentMethods[selectedPaymentMethodIndex].cards.length &&
        allowStoringCard
      const pMethod =
        type === PaymentTypes.CREDIT_CARD
          ? hasStoredCards
            ? 'registered credit card'
            : 'credit card'
          : type === PaymentTypes.PAY_LATER
          ? 'pay later'
          : type === PaymentTypes.COD
          ? 'cash on delivery'
          : type === PaymentTypes.ZERO_YEN
          ? 'zero yen'
          : type === PaymentTypes.PAY_PAY
          ? 'PayPay'
          : type === PaymentTypes.DBARAI
          ? 'dBarai'
          : null

      analytics.virtualPageView('payment information', 'checkout')
      analytics.checkoutView(products, 3, pMethod, null, Sites.zyn)
    }
  }, [selectedPaymentMethodIndex, isOpen, isPaymentIdSet])

  const currentPaymentMethodAvailable = method => {
    switch (method.paymentType) {
      case PaymentTypes.CREDIT_CARD:
        return !!method.paymentId
      case PaymentTypes.PAY_LATER:
        return method.isUserT4 && !method.isUnavailable
      default:
        return true
    }
  }

  const paymentName = paymentType => {
    switch (paymentType) {
      case PaymentTypes.CREDIT_CARD:
        return translations.tk_stepThreeCreditCard
      case PaymentTypes.PAY_LATER:
        return translations.tk_stepThreePaylater
      case PaymentTypes.COD:
        return translations.tk_stepThreeCashDelivery
      case PaymentTypes.PAY_PAY:
        return translations.tk_stepThreePayPay
      case PaymentTypes.DBARAI:
        return translations.tk_stepThreeDBarai
      default:
        return null
    }
  }

  const renderPaymentMethodCreditCard = method => {
    return (
      <CheckoutCreditCard
        translations={translations}
        allowStoringCard={allowStoringCard}
        method={method}
        paymentMethods={paymentMethods}
        selectedPaymentMethodIndex={selectedPaymentMethodIndex}
        hasAdyenPaymentMethod={hasAdyenPaymentMethod}
        clearAdyenCheckoutForm={clearAdyenCheckoutForm}
        setMainStateProperty={setMainStateProperty}
        isOpen={isOpen}
        isReady={isPaymentIdSet}
        toggleStorePaymentMethod={toggleStorePaymentMethod}
        handleDeleteCreditCard={handleDeleteCreditCard}
        handleEditCreditCard={handleEditCreditCard}
        line={line}
      />
    )
  }

  const renderPaymentMethodCreditCardTermsAndCond = method => {
    return method.paymentId ? (
      <Container>
        <TextBody
          size={TextBody.sizes.m}
          {...htmlTranslation(translations, 'tk_stepThreeCreditCardDetails')}
        />
        <Container pt="6" display={Container.displays.flex}>
          <Checkbox
            fullWidth
            checked={termsAndConds.toggleCreditCardTermsAndCond}
            name="toggleCreditCardTermsAndCond"
            template={
              <TextBody
                {...htmlTranslation(
                  translations,
                  'tk_stepThreeCreditCardCheckbox'
                )}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('toggleCreditCardTermsAndCond')
            }
            data-testing="Checkout_Checkbox_CreditCardTerms"
          />
        </Container>
        <Container pt={['6', '2']} pl={['0', '8']}>
          <TextBody
            size={TextBody.sizes.s}
            {...htmlTranslation(
              translations,
              'tk_stepThreeCreditCardCheckBeforeConfirm'
            )}
          />
        </Container>
      </Container>
    ) : null
  }

  const renderPaymentMethodCashOnDelivery = method => {
    return (
      <React.Fragment key={method.paymentId || 0}>
        <Container
          display={
            paymentMethods[selectedPaymentMethodIndex].paymentType ===
            method.paymentType
              ? Container.displays.block
              : Container.displays.none
          }>
          <Container pl="8">
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(
                translations,
                'tk_stepThreePaymentByCashDelivery'
              )}
            />
          </Container>
        </Container>
      </React.Fragment>
    )
  }

  const renderPaymentMethodCashOnDeliveryTermsAndCond = () => {
    return (
      <Container>
        <Container display={Container.displays.flex}>
          <Checkbox
            fullWidth
            checked={termsAndConds.toggleCashOnDeliveryPrivacyStatement}
            name="toggleCashOnDeliveryPrivacyStatement"
            template={
              <TextBody
                {...htmlTranslation(translations, 'tk_stepThreeCashCheckbox1')}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('toggleCashOnDeliveryPrivacyStatement')
            }
            data-testing="Checkout_Checkbox_CashOnDeliveryPrivacy"
          />
        </Container>
        <Container display={Container.displays.flex} pt="4">
          <Checkbox
            fullWidth
            checked={termsAndConds.toggleCashOnDeliveryTermsAndCond}
            name="toggleCashOnDeliveryTermsAndCond"
            template={
              <TextBody
                {...htmlTranslation(translations, 'tk_stepThreeCashCheckbox2')}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('toggleCashOnDeliveryTermsAndCond')
            }
            data-testing="Checkout_Checkbox_CashOnDeliveryTerms"
          />
        </Container>
        <Container pt="6">
          <TextBody
            size={TextBody.sizes.s}
            {...htmlTranslation(
              translations,
              'tk_stepThreeCashCheckBeforeConfirm'
            )}
          />
        </Container>
      </Container>
    )
  }

  const renderPaymentMethodPayLater = method => {
    return (
      <React.Fragment key={method.paymentId}>
        <Container
          display={
            paymentMethods[selectedPaymentMethodIndex].paymentType ===
            method.paymentType
              ? Container.displays.block
              : Container.displays.none
          }>
          {!method.isUserT4 ? (
            <Container
              display={Container.displays.flex}
              alignItems={Container.alignItemsValues.center}
              justifyContent={Container.justifyContentValues.center}
              flexWrap>
              <TextBody
                style={TextBody.styles.error}
                {...htmlTranslation(translations, 'tk_stepThreePaylaterNotT4')}
              />
              <Container pt="4">
                <Button
                  text={translations.tk_stepThreePaylaterNotT4VerifyButton}
                  onClickHandler={() => {
                    goTo(
                      Routes.conversion,
                      true,
                      false,
                      [`callredirectUrl=${Routes.checkout}`],
                      null,
                      Sites.zyn
                    )
                  }}
                  data-testing="Checkout_Button_EmailVerification"
                />
              </Container>
            </Container>
          ) : method.isUnavailable ? (
            <Container
              alignItems={Container.alignItemsValues.center}
              justifyContent={Container.justifyContentValues.center}>
              <TextBody
                style={TextBody.styles.error}
                {...htmlTranslation(
                  translations,
                  'tk_stepThreePaylaterUnvalable'
                )}
              />
            </Container>
          ) : (
            <Container>
              <Container pb="6">
                <TextBody
                  {...htmlTranslation(
                    translations,
                    'tk_stepThreePaylaterDescription'
                  )}
                />
              </Container>
              <Container
                borderColor={Container.borderColors.slate30}
                p={['4', '6']}>
                <Container
                  display={Container.displays.flex}
                  justifyContent={Container.justifyContentValues.between}>
                  <Container pr="2">
                    <Container pb="2">
                      <TextBody
                        {...htmlTranslation(
                          translations,
                          'tk_stepThreePaylaterBlockLeft'
                        )}
                      />
                    </Container>
                    <Container display={Container.displays.flex}>
                      <TextTitle
                        heading={TextTitle.headings.h5}
                        data-testing="Checkout_TextTitle_Phone">
                        {phone}
                      </TextTitle>
                    </Container>
                  </Container>
                  {!isSagawa && (
                    <Container
                      display={Container.displays.flex}
                      justifyContent={Container.justifyContentValues.end}
                      alignItems={Container.alignItemsValues.center}>
                      <Link
                        onClickHandler={handleEditPhoneClick}
                        underlined={false}>
                        <Container
                          display={Container.displays.flex}
                          alignItems={Container.alignItemsValues.center}>
                          <Container pr="2">
                            <Icon
                              size={Icon.sizes.xs}
                              image={Icon.images.address}
                            />
                          </Container>
                          <TextBody size={TextBody.sizes.s}>
                            {translations.tk_stepThreePaylaterBlockRight}
                          </TextBody>
                        </Container>
                      </Link>
                    </Container>
                  )}
                </Container>
              </Container>
            </Container>
          )}
        </Container>
      </React.Fragment>
    )
  }

  const renderPaymentMethodPayLaterTermsAndCond = method => {
    return method.isUserT4 && !method.isUnavailable ? (
      <Container>
        <Container display={Container.displays.flex} pb={['4', '6']}>
          <Checkbox
            fullWidth
            checked={termsAndConds.togglePayLaterPrivacyStatement}
            name="togglePayLaterPrivacyStatement"
            template={
              <TextBody
                {...htmlTranslation(translations, 'tk_stepThreePaylaterTerms1')}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('togglePayLaterPrivacyStatement')
            }
            data-testing="Checkout_Checkbox_PayLaterPrivacy"
          />
        </Container>
        <Container display={Container.displays.flex} pb="6">
          <Checkbox
            fullWidth
            checked={termsAndConds.togglePayLaterTermsAndCond}
            name="togglePayLaterTermsAndCond"
            template={
              <TextBody
                {...htmlTranslation(translations, 'tk_stepThreePaylaterTerms2')}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('togglePayLaterTermsAndCond')
            }
            data-testing="Checkout_Checkbox_PayLaterTerms"
          />
        </Container>
        <Container>
          <TextBody
            style={TextBody.styles.grayedout}
            size={TextBody.sizes.s}
            {...htmlTranslation(translations, 'tk_stepThreePaylaterBottom')}
          />
        </Container>
      </Container>
    ) : null
  }

  const renderPaymentMethodZeroYen = method => {
    return (
      <React.Fragment key={method.paymentId || 0}>
        <Container pt={['4', '0']}>
          <Container display={Container.displays.flex}>
            <Checkbox
              fullWidth
              checked={termsAndConds.toggleZeroYenTermsAndCond}
              name="toggleZeroYenTermsAndCond"
              template={
                <TextBody
                  {...htmlTranslation(translations, 'tk_zeroYenTermsAndCond')}
                />
              }
              onChangeHandler={() =>
                handleCheckboxChange('toggleZeroYenTermsAndCond')
              }
              data-testing="Checkout_Checkbox_ZeroYenTerms"
            />
          </Container>
          <Container pt="6">
            <TextBody
              size={TextBody.sizes.s}
              {...htmlTranslation(translations, 'tk_zeroYenBeforeConfirm')}
            />
          </Container>
        </Container>
      </React.Fragment>
    )
  }

  const renderPaymentMethodPayPay = method => {
    return (
      <React.Fragment key={method.paymentId || 0}>
        <Container
          display={
            paymentMethods[selectedPaymentMethodIndex].paymentType ===
            method.paymentType
              ? Container.displays.block
              : Container.displays.none
          }>
          <Container pl="8">
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(translations, 'tk_stepThreePaymentByPayPay')}
            />
          </Container>
        </Container>
      </React.Fragment>
    )
  }

  const renderPaymentMethodPayPayTermsAndCond = () => {
    return (
      <Container>
        <Container display={Container.displays.flex}>
          <Checkbox
            fullWidth
            checked={termsAndConds.togglePayPayCheckbox1}
            name="togglePayPayCheckbox1"
            template={
              <TextBody
                {...htmlTranslation(
                  translations,
                  'tk_stepThreePayPayCheckbox1'
                )}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('togglePayPayCheckbox1')
            }
            data-testing="Checkout_Checkbox_PayPayCheckbox1"
          />
        </Container>
        <Container display={Container.displays.flex} pt="4">
          <Checkbox
            fullWidth
            checked={termsAndConds.togglePayPayCheckbox2}
            name="togglePayPayCheckbox2"
            template={
              <TextBody
                {...htmlTranslation(
                  translations,
                  'tk_stepThreePayPayCheckbox2'
                )}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('togglePayPayCheckbox2')
            }
            data-testing="Checkout_Checkbox_PayPayCheckbox2"
          />
        </Container>
        <Container pt="6">
          <TextBody
            size={TextBody.sizes.s}
            {...htmlTranslation(
              translations,
              'tk_stepThreePayPayBeforeConfirm'
            )}
          />
        </Container>
      </Container>
    )
  }

  const renderPaymentMethodDBarai = method => {
    return (
      <React.Fragment key={method.paymentId || 0}>
        <Container
          display={
            paymentMethods[selectedPaymentMethodIndex].paymentType ===
            method.paymentType
              ? Container.displays.block
              : Container.displays.none
          }>
          <Container pl="8">
            <TextBody
              size={TextBody.sizes.m}
              {...htmlTranslation(translations, 'tk_stepThreePaymentByDBarai')}
            />
          </Container>
        </Container>
      </React.Fragment>
    )
  }

  const renderPaymentMethodDBaraiTermsAndCond = () => {
    return (
      <Container>
        <Container display={Container.displays.flex}>
          <Checkbox
            fullWidth
            checked={termsAndConds.toggleDBaraiCheckbox1}
            name="toggleDBaraiCheckbox1"
            template={
              <TextBody
                {...htmlTranslation(
                  translations,
                  'tk_stepThreeDBaraiCheckbox1'
                )}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('toggleDBaraiCheckbox1')
            }
            data-testing="Checkout_Checkbox_DBaraiCheckbox1"
          />
        </Container>
        <Container display={Container.displays.flex} pt="4">
          <Checkbox
            fullWidth
            checked={termsAndConds.toggleDBaraiCheckbox2}
            name="toggleDBaraiCheckbox2"
            template={
              <TextBody
                {...htmlTranslation(
                  translations,
                  'tk_stepThreeDBaraiCheckbox2'
                )}
              />
            }
            onChangeHandler={() =>
              handleCheckboxChange('toggleDBaraiCheckbox2')
            }
            data-testing="Checkout_Checkbox_DBaraiCheckbox1"
          />
        </Container>
        <Container pt="6">
          <TextBody
            size={TextBody.sizes.s}
            {...htmlTranslation(
              translations,
              'tk_stepThreePayPayBeforeConfirm'
            )}
          />
        </Container>
      </Container>
    )
  }

  const handleEditPhoneClick = () => {
    goTo(
      Routes.profile,
      true,
      false,
      ['section=phone'],
      Fragments.account,
      Sites.zyn
    )
  }

  const lawReminder = paymentType => {
    switch (paymentType) {
      case PaymentTypes.CREDIT_CARD:
        return 'tk_stepThreeCreditCardLawReminder'
      case PaymentTypes.PAY_LATER:
        return 'tk_stepThreePaylaterLawReminder'
      case PaymentTypes.COD:
        return 'tk_stepThreeCashLawReminder'
      case PaymentTypes.ZERO_YEN:
        return 'tk_zeroYenLawReminder'
      case PaymentTypes.PAY_PAY:
        return 'tk_stepThreePayPayLawReminder'
      case PaymentTypes.DBARAI:
        return 'tk_stepThreeDBaraiLawReminder'
      default:
        return null
    }
  }

  const termsAndCondSection = method => {
    switch (method.paymentType) {
      case PaymentTypes.CREDIT_CARD:
        return renderPaymentMethodCreditCardTermsAndCond(method)
      case PaymentTypes.PAY_LATER:
        return renderPaymentMethodPayLaterTermsAndCond(method)
      case PaymentTypes.COD:
        return renderPaymentMethodCashOnDeliveryTermsAndCond()
      case PaymentTypes.PAY_PAY:
        return renderPaymentMethodPayPayTermsAndCond()
      case PaymentTypes.DBARAI:
        return renderPaymentMethodDBaraiTermsAndCond()
      default:
        return null
    }
  }

  const handleTobaccoPasswordChange = e => {
    setMainStateProperty(e.target.name, e.target.value)
  }

  const renderPaymentMethodBottomArea = () => {
    const selectedPaymentMethod = paymentMethods[selectedPaymentMethodIndex]
    const paymentMethodAvailable =
      selectedPaymentMethod &&
      currentPaymentMethodAvailable(selectedPaymentMethod)

    const termsAndCondBlock = termsAndCondSection(
      paymentMethods[selectedPaymentMethodIndex]
    )

    return (
      paymentMethodAvailable && (
        <Container bg={Container.backgrounds.hardWhite}>
          {termsAndCondBlock && <Container pt="4" />}
          <Container py="6" bg={Container.backgrounds.white}>
            {hasTobacco && (
              <Container px={['4', '6']} pb="6" flexWrap>
                <Container
                  display={Container.displays.flex}
                  justifyContent={Container.justifyContentValues.center}
                  alignItems={Container.alignItemsValues.center}
                  flexDirection={Container.flexDirections.column}>
                  <TextCaption
                    alignment={TextCaption.alignments.center}
                    size={TextCaption.sizes.m}>
                    {translations.tk_password}
                  </TextCaption>
                  <Container>
                    <FieldPassword
                      value={tobaccoPassword}
                      name="tobaccoPassword"
                      onChangeHandler={handleTobaccoPasswordChange}
                      noAutoComplete
                      data-testing="Checkout_FieldPassword_TobaccoPassword"
                    />
                  </Container>
                </Container>
              </Container>
            )}
            {termsAndCondBlock && (
              <Container pb="6" px={['4', '6']}>
                {termsAndCondBlock}
              </Container>
            )}
            <Container
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}>
              <Button
                onClickHandler={purchase}
                disabled={!isPurchaseEnabled}
                text={translations.tk_stepThreeConfirmOrder}
                data-testing="Checkout_Button_ConfirmOrder"
              />
            </Container>
          </Container>
          <Container pt="8" px={['4', '0']}>
            <TextBody
              style={TextBody.styles.grayedout}
              size={TextBody.sizes.s}
              data-testing="Checkout_TextBody_PaymentType"
              {...htmlTranslation(
                translations,
                lawReminder(
                  paymentMethods[selectedPaymentMethodIndex].paymentType
                )
              )}
            />
          </Container>
        </Container>
      )
    )
  }

  const handleOnChangePaymentMethod = e => {
    const index = parseInt(e.target.value || '')
    handlePaymentMethodsChange(index)
  }

  const paymentMethodsItems = paymentMethods.map((el, index) => ({
    value: index,
    label: (
      <Container
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.between}
        alignItems={Container.alignItemsValues.center}
        height="10">
        <TextCaption size={TextCaption.sizes.m}>
          {paymentName(el.paymentType)}
        </TextCaption>
        {paymentMethodsIcons && paymentMethodsIcons[el.paymentType] ? (
          paymentMethodsIcons[el.paymentType].image ? (
            <Container
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.end}
              alignItems={Container.alignItemsValues.center}
              height="12">
              <Image
                src={paymentMethodsIcons[el.paymentType].image}
                alt={el.paymentType}
              />
            </Container>
          ) : (
            paymentMethodsIcons[el.paymentType].icon && (
              <Icon64
                image={paymentMethodsIcons[el.paymentType].icon}
                size={Icon64.sizes.l}
              />
            )
          )
        ) : null}
      </Container>
    ),
    content: (
      <Container fullWidth>
        {el.paymentType === PaymentTypes.CREDIT_CARD &&
          renderPaymentMethodCreditCard(el)}
        {el.paymentType === PaymentTypes.PAY_LATER &&
          renderPaymentMethodPayLater(el)}
        {el.paymentType === PaymentTypes.COD &&
          renderPaymentMethodCashOnDelivery(el)}
        {el.paymentType === PaymentTypes.PAY_PAY &&
          renderPaymentMethodPayPay(el)}
        {el.paymentType === PaymentTypes.DBARAI &&
          renderPaymentMethodDBarai(el)}
      </Container>
    ),
    selected: index === selectedPaymentMethodIndex,
    disabled: false
  }))

  return (
    <Container>
      <Container p={['4', '6']}>
        {paymentMethods[selectedPaymentMethodIndex].paymentType !==
        PaymentTypes.ZERO_YEN ? (
          <Container>
            <Container
              pb="2"
              display={[Container.displays.none, Container.displays.block]}>
              <TextBody
                size={TextBody.sizes.m}
                {...htmlTranslation(
                  translations,
                  'tk_checkoutStepSubtitlePaymentMethod'
                )}
              />
            </Container>
            <RadioGroup
              name="paymentMethods"
              label=""
              style={RadioGroup.styles.border}
              columns={1}
              items={paymentMethodsItems}
              getItemLabel={item => item.label}
              getItemValue={item => item.value}
              getItemContent={item => item.content}
              isItemSelected={item => item.selected}
              onChangeHandler={handleOnChangePaymentMethod}
              showOnlySelectedContent
              spacing={RadioGroup.spacingValues.large}
              data-testing="Checkout_RadioGroup_PaymentMethods"
            />
          </Container>
        ) : (
          <Container fullWidth>
            {renderPaymentMethodZeroYen(
              paymentMethods[selectedPaymentMethodIndex]
            )}
          </Container>
        )}
      </Container>
      {renderPaymentMethodBottomArea()}
    </Container>
  )
}
export default CheckoutStep3
